import {patchState, signalStore, withComputed, withMethods, withProps, withState} from "@ngrx/signals";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {distance} from 'src/app/domain/assets/animals/locations/distance';
import {fourtyEightHours, twentyFourHours} from "src/app/presentation/common/globals";
import {computed, inject} from "@angular/core";
import {FarmStore} from "src/app/domain/assets/farm/farm.store";
import {isValidGpsFixSample} from "src/app/presentation/main-view/map-container/map-utils";

interface LocationState {
  _loadingSamples: boolean,
  assetId: string | undefined,
  locationSamples: Samples.AssetSample<Samples.GPSFix>[]
}

const initialState: LocationState = {
  _loadingSamples: true,
  assetId: undefined,
  locationSamples: []
}

const observedByGateway = (sample: Samples.AssetSample<Samples.GPSFix>) => {
  return sample.sample?.value?.source !== 'PROXIMITY_SAMPLE';
}

const observedByBLE = (sample: Samples.AssetSample<Samples.GPSFix>) => {
  return sample.sample?.value?.source === 'PROXIMITY_SAMPLE';
}

const isReceivedOnOrAfter = (sample: Samples.AssetSample<Samples.GPSFix>, threshold: Date) => {
  const instant = sample.sample.instant;
  if (!instant) return false;

  const date = new Date(instant);
  return date >= threshold;
}

const calculateDistance = (sortedSamples: Samples.AssetSample<Samples.GPSFix>[]) => {
  if (!sortedSamples) return undefined;

  const fourtyEightHoursAgo = new Date(new Date().getTime() - fourtyEightHours);
  const samples = sortedSamples.filter(sample => {
    const instant = sample.sample?.instant;
    if (!instant) return false;

    const coordinates = sample.sample?.value?.position?.coords;
    if (!coordinates || coordinates[0] === 0 || coordinates[1] === 0) return false;

    return new Date(instant) >= fourtyEightHoursAgo;
  });

  if (samples.length < 2) return undefined;

  let total = 0;
  for (var i = 1; i < samples.length; ++i) {
    const start = samples[i - 1].sample?.value?.position?.coords;
    const end = samples[i].sample?.value?.position?.coords;
    total += distance(start, end);
  }

  return total;
}

export const LocationStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withProps(() => ({
    farmStore: inject(FarmStore)
  })),
  withMethods(store => ({
    setAssetId(assetId?: string) {
      patchState(store, { assetId, _loadingSamples: true });
    },
    setLocationSamples(locationSamples: Samples.AssetSample<Samples.GPSFix>[]) {
      const validSamples = locationSamples.filter(isValidGpsFixSample);
      patchState(store, { locationSamples: validSamples, _loadingSamples: false })
    }
  })),
  withComputed(({ _loadingSamples, farmStore }) => ({
    loading: computed(() => {
      return farmStore.loading() || _loadingSamples()
    })
  })),
  withComputed(({ loading, locationSamples, farmStore }) => ({
    distance: computed(() => {
      if (loading()) return undefined;

      const farm = farmStore.farm();
      if (!farm) return undefined;

      const distanceInMeters = calculateDistance(locationSamples());
      if (distanceInMeters === undefined) return undefined;

      const countryCode = farm.address.countryCode;
      if (countryCode === 'US') {
        const distanceInYards = Math.round(distanceInMeters / 0.9144);
        return `${distanceInYards} yds`;
      }

      return `${Math.round(distanceInMeters)} m`;
    }),
    numberOfGpsPositionsLast24Hours: computed(() => {
      if (loading()) return undefined;

      const twentyFourHoursAgo = new Date(new Date().getTime() - twentyFourHours);

      return locationSamples()
        .filter(observedByGateway)
        .filter(sample => isReceivedOnOrAfter(sample, twentyFourHoursAgo))
        .length
    }),

    lastGpsLocationSample: computed(() => {
      if (loading()) return undefined;
      return locationSamples().find(observedByGateway);
    }),

    lastBLELocationSample: computed(() => {
      if (loading()) return undefined;
      return locationSamples().find(observedByBLE);
    })

  })),
  withComputed(({ lastGpsLocationSample }) => ({
    numberOfSatellites: computed(() => {
      const sample = lastGpsLocationSample();
      if (!sample) return undefined;

      return sample.sample.value.numberOfSatellites;
    }),
    timeToFix: computed(() => {
      const sample = lastGpsLocationSample();
      if (!sample) return undefined;

      return sample.sample.value.timeToFix;
    })
  }))
);
